import React, { useMemo, useState } from 'react';
import {Dialog, DialogTitle, DialogContent, DialogContentText, TextField, Divider, Button, Typography, DialogActions, makeStyles} from "@material-ui/core"

import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DoneOutlineIcon from '@material-ui/icons/DoneOutline';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';
import { SaveBuilderAssetRequest, BuilderAssetService } from '../../../../services/BuilderAssetService';
import { WordFileService } from '../../../../services/WordFileService';
import { UserAgent } from '../../../../utilities/UserAgent';
import { useLibrary, useLibraryAlert } from '../../LibraryPage';
import { AssetCategoryType } from '../../../../entities/AssetType';
import { useIsMobile } from '../../../../components/Mui3WidthProvider';
import { FieldValidationError, ValidationHelper } from '../../../../services/server/ServerResponses';
import { convertBuilderAssetToLibraryItem } from '../../../../entities/BuilderAsset';

const useStyles = makeStyles((theme) => ({
    uploadFileButton: {
        width: '100%',
        height: '300px',
        marginBottom: theme.spacing(2)
    },
    uploadFilePlaceholderText: {
        fontFamily: "Roboto, Helvetica",
        fontSize: "12px",
        marginTop: "12px",
        color: "#5f6368",
        textAlign: 'center'
    },
    uploadFilePlaceholderIcon: {
        width: "90px",
        height: "90px",
        color: "#CCC"
    },
}));

interface Props {
    open: boolean;
	selectedAssetType: AssetCategoryType;
    onClose: () => void;
    onSuccess: () => void;
}

export function AddDocxDialog(props:Props) {
    const { selectedAssetType } = props;
    const context = useLibrary();
    const isMobile = useIsMobile();
    const libraryAlert = useLibraryAlert();
    const classes = useStyles();

    const [fileName, setFileName] = useState("");
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const [uploading, setUploading] = useState(false);
    const [fileData, setFileData] = useState<string>();
    const uploadError = useMemo(() => ValidationHelper.getFieldErrorSummary(errors, "Upload"), [errors]);

    const handleNameChange = (e:React.ChangeEvent<HTMLInputElement>) => {
        setErrors([]);
        setFileName(e.currentTarget.value)
    }

    const onFileUploadSubmit = async(e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if(fileName.length === 0)
            return setErrors([{field: "Name", errors: ["Invalid File Name"]}]);

        if(!fileData)
            return;

        const request: SaveBuilderAssetRequest = {
            parentId: context.activeFolderId ?? undefined,
            name: fileName,
            isStarred: false,
            dateAccessed: new Date(),
            category: selectedAssetType.category,
            text: fileData,
            slideCount: 0,
            isTemplate: false,
            templateDescription: '',
            slideExporterAspectRatio: undefined,
            customSlideBackgroundImagePath: undefined,
            offlineMode: false,
            lastModifiedFormFactor: UserAgent.getFormFactor(),
            lastModifiedOnOperatingSystem: UserAgent.getOperatingSystem(),
        };

        setUploading(true)
        const response = await BuilderAssetService.save(request);
        setUploading(true)

        if(response.validation) return setErrors(response.errors);
        if(response.error) return libraryAlert(response.message);

		const asset = response.data;
		context.addLibraryItem(convertBuilderAssetToLibraryItem(asset));
		libraryAlert(`${asset.name} created`, async () => {
			const result = await BuilderAssetService.remove(asset.id);
			if(result.success) {
				context.removeLibraryItem(asset.id);
				libraryAlert(`Removed ${asset.name}`);
			} else {
				libraryAlert(`Failed to delete ${asset.name}`);
			}
		});
		props.onSuccess();
    }

    const onFileUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0]
        if(file)
            onFileSelected(file);
    }

    const handleFileUploadDrop = (e: React.DragEvent<HTMLElement>) => {
        const file = e.dataTransfer.files[0]
        if(file)
            onFileSelected(file)
    }

    const onFileSelected = async(file: File) => {
        setErrors([])

        if (file.size >= 26214400)
            return setErrors([{errors: ["File Too Large"], field: "Upload" }]);

        if (!WordFileService.isDocxFile(file))
            return setErrors([{errors: ["File must be .docx"], field: "Upload" }]);
        
        setUploading(true)
        const result = await WordFileService.parseDocxFile(file);
        setUploading(false)

        if(result.success) return setFileData(result.data.html);
        if(result.error) return libraryAlert(result.message);
    }

    return (
        <Dialog 
            open={props.open} 
            onClose={() => props.onClose()}
            fullScreen={isMobile}
        >
            <DialogTitle id="form-dialog-title">Upload Word Document
            {isMobile && <CloseIcon style={{ float: "right", color: "rgb(0, 0, 0, .4)"}} onClick={props.onClose} />}
            </DialogTitle>
            <form onSubmit={onFileUploadSubmit}>
                <DialogContent>
                    <DialogContentText>
                        Enter the name of your new word document.
                    </DialogContentText>
                    <TextField
                        required
                        autoFocus={true}
                        margin="dense"
                        id="newItemName"
                        label="Name"
                        type="text"
                        onChange={handleNameChange}
                        value={fileName}
                        fullWidth
                        error={ValidationHelper.isFieldInError(errors, 'Name')}
                        helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Name') }} />}
                    />
                    <Divider />
                    <input
                        style={{display: "none"}}
                        id="flat-button-file"
                        onChange={(e) => onFileUploadChange(e)}
                        accept=".docx"
                        type="file"
                        disabled={uploading}
                    />
                    <label htmlFor="flat-button-file">
                        <Button
                            disabled={uploading}
                            variant={fileData && fileData.length > 0 ? 'outlined' : 'text'}
                            color={fileData && fileData.length > 0 ? 'primary' : 'secondary'}
                            onDragOver={(e:any) => e.preventDefault()}
                            onDrop={(e:any) => handleFileUploadDrop(e)}
                            component="span"
                            className={classes.uploadFileButton}
                        >
	                        {fileData && !uploadError
                                &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <DoneOutlineIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    Document Accepted
                                </Typography>
                            }
                            { !fileData && !uploadError &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <CloudUploadIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    {uploading ? "Uploading File..." : "Upload Here" }
                                </Typography>
                            }
                            { uploadError &&
                                <Typography className={classes.uploadFilePlaceholderText}>
                                    <ErrorIcon className={classes.uploadFilePlaceholderIcon} />
                                    <br />
                                    {ValidationHelper.getFieldErrorSummary(errors, "Upload")}
                                </Typography>
                            }
                        </Button>
                    </label>
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose} color="primary">
                        Cancel
                    </Button>
                    {
                        uploading
                        ? <Button color='primary' disabled>Uploading File</Button>
                        : <Button color='primary' disabled={!fileData || errors.length !== 0 } type="submit">Upload</Button>
                    }
                </DialogActions>
            </form>
        </Dialog>
    );
}